import React, { useEffect, useState } from "react";
import { BASE_URL } from "utils/constants";

// Define proper TypeScript interfaces
interface Customer {
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface Order {
  order_number: string;
  id: string; // Added orderId field
  customer?: Customer;
  total_price: string;
  fulfillment_status?: string;
}

interface SavedOrder {
  number: string;
  orderId: string;
}

const OrdersTable = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<{ number: string; id: string }[]>([]);
  const [savedOrders, setSavedOrders] = useState<SavedOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate available orders (not already saved)
  const availableOrders = orders.filter(
    (order) => !savedOrders.some((savedOrder) => savedOrder.number === order.order_number)
  );

  const allSelected = selectedOrders.length === availableOrders.length && availableOrders.length > 0;

  useEffect(() => {
    fetchData();
    getSavedOrders();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${BASE_URL}/shopify/unfulfilled-orders`);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      setOrders(data?.orders || []);
      console.log(data);
    } catch (error) {
      console.error("Error fetching Shopify orders:", error);
      setError("Failed to fetch orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getSavedOrders = async () => {
    try {
      const res = await fetch(`${BASE_URL}/shopify/stored-orders`);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      setSavedOrders(data?.orders || []);
    } catch (error) {
      console.error("Error fetching saved orders:", error);
      // Don't show error for saved orders as it's not critical
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(
        availableOrders.map((item) => ({
          number: item.order_number.toString(),
          id: item.id,
        }))
      );
    }
  };

  const handleRowSelect = (order: Order) => {
    const orderInfo = {
      number: order.order_number.toString(),
      id: order.id,
    };

    if (selectedOrders.some((selected) => selected.number === orderInfo.number)) {
      setSelectedOrders(selectedOrders.filter((selected) => selected.number !== orderInfo.number));
    } else {
      setSelectedOrders([...selectedOrders, orderInfo]);
    }
  };

  const sendOrderToGBF = async () => {
    if (selectedOrders.length === 0) {
      alert("Please select at least one order");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/shopify/send-order-to-gbf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orders: selectedOrders,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data?.message) {
        alert(data?.message);
        setSelectedOrders([]);
        // Refresh data after successful send
        await Promise.all([fetchData(), getSavedOrders()]);
      }
    } catch (error) {
      console.error("Error sending orders:", error);
      alert("Failed to send orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isOrderSaved = (orderNumber: string) => {
    return savedOrders.some((savedOrder) => savedOrder.number.toString() === orderNumber.toString());
  };

  if (loading && orders.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-min-h-64">
        <div className="tw-text-center">
          <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-[#FF7B6D] tw-mx-auto"></div>
          <p className="tw-mt-4 tw-text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
      <div className="tw-w-full tw-max-w-5xl tw-overflow-x-auto tw-p-4 tw-bg-white tw-shadow-lg tw-rounded">
        {error && (
          <div className="tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-mb-4">
            {error}
            <button onClick={fetchData} className="tw-ml-2 tw-underline hover:tw-no-underline">
              Retry
            </button>
          </div>
        )}

        <div className="tw-flex tw-justify-end tw-mb-4">
          <button
            disabled={selectedOrders.length === 0 || loading}
            onClick={sendOrderToGBF}
            className={`${
              selectedOrders.length === 0 || loading ? "tw-bg-gray-400" : "tw-bg-[#FF7B6D] hover:tw-bg-[#e66b5d]"
            } tw-text-white tw-px-4 tw-py-2 tw-rounded tw-transition-colors tw-duration-200 tw-disabled:cursor-not-allowed`}
          >
            {loading ? "Sending..." : "Send Orders"}
          </button>
        </div>

        <table className="tw-w-full tw-text-left tw-border-collapse">
          <thead>
            <tr className="tw-bg-gray-200">
              <th className="tw-p-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={availableOrders.length === 0 || loading}
                  onChange={handleSelectAll}
                  className="tw-cursor-pointer disabled:tw-cursor-not-allowed"
                />
              </th>
              <th className="tw-p-2">#</th>
              <th className="tw-p-2">Order Number</th>
              <th className="tw-p-2">Customer Name</th>
              <th className="tw-p-2">Customer Email</th>

              <th className="tw-p-2">Total Price</th>
              <th className="tw-p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(orders) && orders.length > 0 ? (
              orders.map((order, index) => {
                const isSaved = isOrderSaved(order.order_number.toString());
                return (
                  <tr
                    key={order.order_number}
                    className={`tw-border-b ${isSaved ? "tw-bg-green-50 tw-text-gray-500" : "hover:tw-bg-gray-100"}`}
                  >
                    <td className="tw-p-2">
                      <input
                        type="checkbox"
                        checked={selectedOrders.some((selected) => selected.number === order.order_number.toString())}
                        disabled={isSaved || loading}
                        onChange={() => handleRowSelect(order)}
                        className="tw-cursor-pointer disabled:tw-cursor-not-allowed"
                      />
                    </td>
                    <td className="tw-p-2">{index + 1}</td>
                    <td className="tw-p-2">#{order.order_number}</td>
                    <td className="tw-p-2">
                      {order.customer?.first_name || ""} {order.customer?.last_name || ""}
                    </td>
                    <td className="tw-p-2">{order.customer?.email || ""}</td>
                    <td className="tw-p-2">${order?.total_price}</td>
                    <td className="tw-p-2">
                      {isSaved ? (
                        <span className="tw-text-green-600 tw-font-medium">✓ Sent</span>
                      ) : (
                        <span className="tw-text-[#FF7B6D]"></span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="tw-text-center tw-p-4 tw-text-gray-500">
                  {loading ? "Loading orders..." : "No unfulfilled orders found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrdersTable;
